'use client'

import { Identities, ResponseTypes, type SignInWithPK } from '@/features/Auth/auth.types'
import { clearAuthStorage, saveUser } from '@/features/Auth/auth.utils'
import { cancelPendingRequests } from '@/features/Client/Client'
import { authService } from '@/services'
import { passKeyService } from '@/services/passkey.service'
import { parseError } from '@/shared/helpers/error'
import { trackUserAction } from '@/shared/helpers/sentry'
import { useAppWallet } from '@/shared/wallet/useAppWallet'
import { useAuthStore } from '@/store'
import * as Sentry from '@sentry/nextjs'
import { AxiosError } from 'axios'
import { useRouter } from 'next/navigation'
import { useCallback, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'

interface Props {
  callback?: () => void;
  dappCallback?: () => void;
  passKeyCallback?: () => void;
  totpCallback?: () => void;
  walletType: string;
}

export { getUser, saveUser } from '@/features/Auth/auth.utils'

export function useLogin({ callback, totpCallback, passKeyCallback, dappCallback, walletType }: Props) {
  const setUserId = useAuthStore((state) => state.setUserId)

  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const wallet = useAppWallet(walletType)

  const call = useCallback(async () => {
    setIsLoading(true)
    trackUserAction('Sign-in attempt started', { wallet: walletType })

    try {
      const response = await authService.signInWithPublicKey({
        publicKey: wallet.publicKey,
        signTx: wallet.signTransaction as SignInWithPK['signTx'],
        signMessage: wallet.signMessage,
        confirmType: wallet.confirmType,
      })

      if ('accessToken' in response) {
        try {
          saveUser({ accessToken: response.accessToken, refreshToken: response.refreshToken })
        } catch (saveError) {
          console.error('Failed to save user to localStorage:', saveError)
          setError(saveError)
          Sentry.captureException(saveError)
          setIsLoading(false)
          return
        }
        trackUserAction('Sign-in succeeded', { wallet: walletType })
        if (typeof callback === 'function') callback()
        setIsLoading(false)
        return
      }

      if ('type' in response && response.type === ResponseTypes.SWPO_WITH_SECOND_FACTOR) {
        setUserId(response.userId)
        if (response.requiredSecondFactor === Identities.PASSKEY) passKeyCallback()
        if (response.requiredSecondFactor === Identities.TOTP) totpCallback()
        setIsLoading(false)
        return
      }

      if ('type' in response && (response.type === ResponseTypes.VerifyPublicKeyForEmbeddedBrowser || response.type === ResponseTypes.SETUP_TOTP_IMMEDIATELY)) {
        setUserId(response.userId)
        dappCallback()
        setIsLoading(false)
        return
      }

      if ('requiredSecondFactor' in response) {
        setUserId(response.userId)
        if (response.requiredSecondFactor === Identities.PASSKEY) passKeyCallback()
        if (response.requiredSecondFactor === Identities.TOTP) totpCallback()
        setIsLoading(false)
        return
      }
    } catch (e) {
      console.error(e)
      if (e instanceof AxiosError && e?.response?.status === 404) {
        trackUserAction('User not found, attempting sign-up', { wallet: walletType })
        try {
          const res = await authService.signUpWithPublicKey({
            publicKey: wallet.publicKey,
            signTx: wallet.signTransaction as SignInWithPK['signTx'],
            signMessage: wallet.signMessage,
            confirmType: wallet.confirmType,
          })

          if ('accessToken' in res) {
            try {
              saveUser({ accessToken: res.accessToken, refreshToken: res.refreshToken })
            } catch (saveError) {
              console.error('Failed to save user to localStorage:', saveError)
              setError(saveError)
              Sentry.captureException(saveError)
              parseError(saveError)
              setIsLoading(false)
              return
            }
          }

          trackUserAction('Sign-up succeeded', { wallet: walletType })
          if (typeof callback === 'function') {
            callback()
          }
          setIsLoading(false)
        } catch (eInner) {
          setError(eInner)
          Sentry.captureException(eInner)
          parseError(eInner)
          setIsLoading(false)
        }

        return
      }
      setError(e)
      Sentry.captureException(e)
      parseError(e)
    }

    setIsLoading(false)
  }, [callback, dappCallback, passKeyCallback, setUserId, totpCallback, wallet, walletType])

  return { isLoading, error, call }
}

export function useLogout() {
  const router = useRouter()
  const queryClient = useQueryClient()
  const setSelectedPublicKey = useAuthStore((state) => state.setSelectedPublicKey)
  const setSelectedWalletType = useAuthStore((state) => state.setSelectedWalletType)
  const setUserId = useAuthStore((state) => state.setUserId)
  const selectedWalletType = useAuthStore((state) => state.selectedWalletType)
  const wallet = useAppWallet(selectedWalletType)

  const logout = useCallback(async () => {
    if (wallet.connected) await wallet.disconnect()
    await queryClient.cancelQueries()
    cancelPendingRequests()
    clearAuthStorage()
    queryClient.clear()
    setSelectedPublicKey('')
    setSelectedWalletType('')
    setUserId(null)
    Sentry.setUser(null)
    router.replace('/auth/signin')
  }, [queryClient, router, setSelectedPublicKey, setSelectedWalletType, setUserId, wallet])

  return { logout }
}

export function usePasskey() {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const userId = useAuthStore((state) => state.userId)

  const login = useCallback(async () => {
    setIsLoading(true)
    trackUserAction('User clicked Passkey sign-in')
    try {
      const response = await passKeyService.login(userId)
      if ('error' in response) {
        setError(response.error)
        Sentry.captureException(response.error)
        setIsLoading(false)
        return { error: response.error }
      }
      if ('accessToken' in response) {
        // saveUserToCookie(response)
        saveUser({ accessToken: response.accessToken, refreshToken: response.refreshToken })
      }
    } catch (e) {
      setError(e)
      Sentry.captureException(e)
    }
    setIsLoading(false)
  }, [userId])

  return { isLoading, error, login }
}
